import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import type { PricingPlan, SubscriptionModalProps } from './types';
import { addAccessUser, addUser, login } from '../../services/user';

/**
 * SubscriptionModal Component
 * Displays pricing plans with plan selection and checkout flow
 */
export const SubscriptionModal: React.FC<SubscriptionModalProps> = ({
  isOpen,
  onClose,
  onSubscribe,
}) => {
  const dispatch = useDispatch();
  const currentUser = useSelector((state: any) => state.user.currentUser);
  const [selectedPlan, setSelectedPlan] = useState<'monthly' | 'quarterly'>('quarterly');
  const [isLoading, setIsLoading] = useState(false);
  const [step, setStep] = useState<'plan' | 'auth' | 'login' | 'register' | 'confirm'>('plan');
  
  // Registro
  const [registerData, setRegisterData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [registerError, setRegisterError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [newUserData, setNewUserData] = useState<{
    firstName?: string;
    lastName?: string;
    fullName?: string;
    username?: string;
    email?: string;
  } | null>(null);

  // Login
  const [loginData, setLoginData] = useState({
    email: '',
    password: '',
  });
  const [loginError, setLoginError] = useState('');
  const [showLoginPassword, setShowLoginPassword] = useState(false);

  // Close on ESC key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Prevent scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Reset flow each time modal opens
  useEffect(() => {
    if (isOpen) {
      setStep(currentUser ? 'plan' : 'plan');
      setIsLoading(false);
      setRegisterError('');
      setLoginError('');
      setNewUserData(null);
      setLoginData({ email: '', password: '' });
      setRegisterData({
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        confirmPassword: '',
      });
    }
  }, [isOpen, currentUser]);

  const plans: Record<'monthly' | 'quarterly', PricingPlan> = {
    monthly: {
      id: 'monthly',
      period: 'Mensual',
      price: 1111,
      pricePerMonth: 1111,
      billingInfo: 'por mes · cobro mensual',
      features: [
        '🌿 1 Masterclass con la temática del mes',
        '🌸 1 Círculo de Palabra (sesión grupal en vivo)',
        '🔥 1 Sesión especial (meses nones)',
        '🌕 1 Invitada especial (meses pares)',
        '🎧 2 Audios activadores (mañana y noche)',
        '✍️ Journal prompts semanales',
        '🕯️ Ritual semanal',
      ],
    },
    quarterly: {
      id: 'quarterly',
      period: '3 Meses',
      price: 3000,
      pricePerMonth: 1000,
      billingInfo: 'por mes · cobro único de $3,000',
      features: [
        '🌸 Acceso completo a la Membresía Mujer Soberana',
        '✨ Precio preferencial',
        '✨ Compromiso con tu proceso',
        '✨ Acceso gratuito a la plataforma de yoga (más de 150 clases grabadas)',
      ],
      savings: {
        percent: 10,
        amount: 333,
      },
    },
  };

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleRegisterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setRegisterData(prev => ({ ...prev, [name]: value }));
    setRegisterError('');
  };

  const validateRegisterForm = () => {
    if (!registerData.firstName.trim()) return 'Por favor ingresa tu nombre';
    if (!registerData.lastName.trim()) return 'Por favor ingresa tu apellido';
    if (!registerData.email.trim()) return 'Por favor ingresa tu email';
    if (!registerData.email.includes('@')) return 'Email inválido';
    if (!registerData.password || registerData.password.length < 6) return 'La contraseña debe tener al menos 6 caracteres';
    if (registerData.password !== registerData.confirmPassword) return 'Las contraseñas no coinciden';
    return '';
  };

  const handleRegister = async () => {
    const error = validateRegisterForm();
    if (error) {
      setRegisterError(error);
      return;
    }

    setIsLoading(true);
    try {
      const fullName = `${registerData.firstName} ${registerData.lastName}`.trim();
      const username = registerData.email.split('@')[0]?.toLowerCase() || registerData.email.toLowerCase();

      const accessUserPayload = {
        fullName,
        username,
        email: registerData.email.toLowerCase(),
        password: registerData.password,
      };

      const profileUserPayload = {
        fullName,
        username,
        email: registerData.email.toLowerCase(),
        subscriptionPlanId: [],
      };

      const accessResponse: any = await addAccessUser(dispatch, accessUserPayload);
      
      if (accessResponse?.status === 201) {
        const profileResponse: any = await addUser(dispatch, profileUserPayload);

        if (profileResponse?.response) {
          setRegisterError('La cuenta de acceso fue creada, pero falló el perfil. Por favor inicia sesión.');
          return;
        }

        setNewUserData({
          firstName: registerData.firstName,
          lastName: registerData.lastName,
          email: registerData.email.toLowerCase(),
        });
        setStep('confirm');
      } else if (accessResponse?.response?.data?.message) {
        setRegisterError(accessResponse.response.data.message);
      } else if (accessResponse?.response?.data) {
        setRegisterError(accessResponse.response.data);
      } else {
        setRegisterError('Error al crear la cuenta. Intenta con otro email.');
      }
    } catch (err: any) {
      setRegisterError(err?.response?.data?.message || 'Error en el servidor');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLoginChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setLoginData(prev => ({ ...prev, [name]: value }));
    setLoginError('');
  };

  const handleLogin = async () => {
    if (!loginData.email.trim()) {
      setLoginError('Por favor ingresa tu email');
      return;
    }
    if (!loginData.password) {
      setLoginError('Por favor ingresa tu contraseña');
      return;
    }

    setIsLoading(true);
    try {
      const response = await login(dispatch, loginData);
      
      if (response && !response?.response) {
        // Éxito en login - ir a confirmación
        setStep('confirm');
        setLoginData({ email: '', password: '' });
      } else {
        setLoginError('Email o contraseña incorrectos');
      }
    } catch (err: any) {
      setLoginError(err?.response?.data?.message || 'Error al iniciar sesión');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubscribe = async () => {
    if (!currentUser) {
      setStep('auth');
      return;
    }

    // Si está logueado, ir a confirmación
    setStep('confirm');
  };

  const handleConfirmAndCheckout = () => {
    setIsLoading(true);
    try {
      onSubscribe(selectedPlan);
      setTimeout(() => {
        setIsLoading(false);
      }, 1200);
    } catch (err) {
      setIsLoading(false);
    }
  };

  const getDisplayName = () => {
    const newUserName = [newUserData?.firstName, newUserData?.lastName]
      .filter(Boolean)
      .join(' ')
      .trim();
    if (newUserName) return newUserName;

    const currentUserName = [currentUser?.firstName, currentUser?.lastName]
      .filter(Boolean)
      .join(' ')
      .trim();
    if (currentUserName) return currentUserName;

    if (typeof currentUser?.fullName === 'string' && currentUser.fullName.trim()) {
      return currentUser.fullName.trim();
    }

    if (typeof currentUser?.username === 'string' && currentUser.username.trim()) {
      return currentUser.username.trim();
    }

    const email =
      newUserData?.email ||
      currentUser?.email ||
      loginData.email;
    if (typeof email === 'string' && email.includes('@')) {
      return email.split('@')[0];
    }

    return 'Mujer Soberana';
  };

  if (!isOpen) return null;

  const activePlan = plans[selectedPlan];
  const monthlyPlan = plans['monthly'];
  const quarterlyPlan = plans['quarterly'];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      onClick={handleBackdropClick}
    >
      {/* Modal Box */}
      <div className="w-full max-w-xl bg-white rounded-3xl overflow-hidden shadow-2xl transform transition-transform duration-300 animate-fadeIn">
        {/* Header */}
        <div className="relative bg-gradient-to-br from-sb-800 via-sb-700 to-sb-600 px-8 pt-8 pb-7 overflow-hidden">
          {/* Decorative elements */}


          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white/10 border border-white/20 flex items-center justify-center transition-all hover:bg-white/20 text-white/75 hover:text-white"
            aria-label="Close modal"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 14 14"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
            >
              <path d="M1 1L13 13M13 1L1 13" />
            </svg>
          </button>

          {/* Content */}
          <div className="relative z-10">
            <div className="text-so-300 text-xs font-semibold tracking-widest uppercase mb-2">
              ✦ MEMBRESÍA MUJER SOBERANA
            </div>
            <p className="text-white/80 text-sm font-light leading-relaxed max-w-sm">
              Accede a clases, meditaciones, breathwork y recursos exclusivos para tu transformación.
            </p>
          </div>
        </div>

        {/* Body */}
        <div className="px-8 pt-7 pb-8 bg-white max-h-[75vh] overflow-y-auto">
          {step === 'plan' ? (
            <>
          {/* Plans Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            {/* Monthly Plan */}
            <button
              onClick={() => setSelectedPlan('monthly')}
              className={`relative border-2 rounded-2xl p-5 transition-all text-left bg-white ${
                selectedPlan === 'monthly'
                  ? 'border-so-600 shadow-lg ring-2 ring-so-600/20'
                  : 'border-gray-200 hover:border-sb-300 hover:shadow-md'
              }`}
            >
              {/* Radio */}
              <div
                className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mb-3 transition-all ${
                  selectedPlan === 'monthly'
                    ? 'border-so-600 bg-so-600'
                    : 'border-gray-300 bg-white'
                }`}
              >
                {selectedPlan === 'monthly' && (
                  <div className="w-2.5 h-2.5 rounded-full bg-white" />
                )}
              </div>

              <div className="text-sb-600 text-xs font-semibold tracking-widest uppercase mb-1">
                {monthlyPlan.period}
              </div>
              <div className="text-2xl font-semibold text-gray-900 leading-tight mb-1">
                <sup className="text-sm font-normal text-gray-600">$</sup>
                {monthlyPlan.price.toLocaleString('es-MX')}
                <span className="text-sm font-normal text-gray-500">/mes</span>
              </div>
              <div className="text-gray-500 text-xs mb-3">cobro mensual · cancela en cualquier momento</div>

              <div className="h-px bg-gray-200 mb-3" />

              {/* Features */}
              <ul className="space-y-2">
                {monthlyPlan.features.map((feature) => (
                  <li key={feature} className="flex items-center gap-2">
                    <div
                      className={`w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 ${
                        selectedPlan === 'monthly' ? 'bg-sb-200' : 'bg-sb-200'
                      }`}
                    >
                      <svg
                        width="9"
                        height="7"
                        viewBox="0 0 9 7"
                        fill="none"
                        stroke="#B14A36"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                      >
                        <path d="M1 3.5L3.2 6L8 1" />
                      </svg>
                    </div>
                    <span className="text-xs text-gray-700 font-normal">{feature}</span>
                  </li>
                ))}
              </ul>
            </button>

            {/* Quarterly Plan */}
            <button
              onClick={() => setSelectedPlan('quarterly')}
              className={`relative border-2 rounded-2xl p-5 transition-all text-left bg-white ${
                selectedPlan === 'quarterly'
                  ? 'border-so-600 shadow-lg ring-2 ring-so-600/20'
                  : 'border-gray-200 hover:border-sb-300 hover:shadow-md'
              }`}
            >
              {/* Badge */}
              {quarterlyPlan.savings && (
                <div className="absolute -top-0.5 right-6 px-3 py-1 bg-gradient-to-r from-so-600 to-so-400 rounded-b-xl text-white text-xs font-bold tracking-wide shadow-md uppercase">
                  MEJOR VALOR
                </div>
              )}

              {/* Radio */}
              <div
                className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mb-3 transition-all ${
                  selectedPlan === 'quarterly'
                    ? 'border-so-600 bg-so-600'
                    : 'border-gray-300 bg-white'
                }`}
              >
                {selectedPlan === 'quarterly' && (
                  <div className="w-2.5 h-2.5 rounded-full bg-white" />
                )}
              </div>

              <div className="text-sb-600 text-xs font-semibold tracking-widest uppercase mb-1">
                {quarterlyPlan.period}
              </div>
              <div className="text-2xl font-semibold text-gray-900 leading-tight mb-1">
                <sup className="text-sm font-normal text-gray-600">$</sup>
                {quarterlyPlan.pricePerMonth.toLocaleString('es-MX')}
                <span className="text-sm font-normal text-gray-500">/mes</span>
              </div>
              <div className="text-gray-500 text-xs mb-2">cobro único de ${quarterlyPlan.price.toLocaleString('es-MX')} · ahorra ${quarterlyPlan.savings?.amount}</div>

              {/* Savings Badge */}
              {quarterlyPlan.savings && (
                <div className="inline-flex items-center gap-1.5 text-green-700 text-xs font-semibold bg-green-50 border border-green-200 px-2.5 py-1 rounded-lg mb-3">
                  <svg
                    width="10"
                    height="10"
                    viewBox="0 0 12 12"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  >
                    <circle cx="6" cy="6" r="4.5" />
                  </svg>
                  Ahorra {quarterlyPlan.savings.percent}%
                </div>
              )}

              <div className="h-px bg-gray-200 mb-3" />

              {/* Features */}
              <ul className="space-y-2">
                {quarterlyPlan.features.map((feature) => (
                  <li key={feature} className="flex items-center gap-2">
                    <div
                      className={`w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 ${
                        selectedPlan === 'quarterly' ? 'bg-so-200' : 'bg-sb-200'
                      }`}
                    >
                      <svg
                        width="9"
                        height="7"
                        viewBox="0 0 9 7"
                        fill="none"
                        stroke="#B14A36"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                      >
                        <path d="M1 3.5L3.2 6L8 1" />
                      </svg>
                    </div>
                    <span className="text-xs text-gray-700 font-normal">{feature}</span>
                  </li>
                ))}
              </ul>
            </button>
          </div>


          {/* CTA Button */}
          <button
            onClick={handleSubscribe}
            disabled={isLoading}
            className="w-full py-4 bg-gradient-to-r from-so-600 to-so-400 text-white rounded-xl font-semibold text-sm tracking-wide transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5 disabled:opacity-70 disabled:cursor-not-allowed"
          >
            {isLoading
              ? '✓ Redirigiendo al pago seguro…'
              : currentUser
                ? `Continuar al pago — ${activePlan.period} · $${activePlan.pricePerMonth.toLocaleString('es-MX')}/mes`
                : `Continuar — ${activePlan.period} · $${activePlan.pricePerMonth.toLocaleString('es-MX')}/mes`}
          </button>

          {/* Legal Text */}
          <div className="text-center text-xs text-gray-500 mt-4 leading-relaxed">
            Al continuar aceptas nuestros{' '}
            <a href="#" className="text-sb-600 hover:underline">
              Términos
            </a>{' '}
            y{' '}
            <a href="#" className="text-sb-600 hover:underline">
              Política de Privacidad
            </a>
            . Pago seguro vía Stripe.
          </div>
            </>
          ) : step === 'auth' ? (
            <>
              <div className="mb-4">
                <button
                  onClick={() => setStep('plan')}
                  className="inline-flex items-center gap-2 text-xs font-semibold tracking-wider uppercase text-sb-600 hover:text-sb-800"
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M15 18l-6-6 6-6" />
                  </svg>
                  Regresar a planes
                </button>
              </div>

              <div className="rounded-2xl border border-sb-200 bg-sb-50 p-4 mb-6">
                <p className="text-xs uppercase tracking-widest text-sb-600 font-semibold mb-1">Plan seleccionado</p>
                <p className="text-base font-semibold text-gray-900">
                  {selectedPlan === 'monthly' ? 'Mensual' : '3 Meses'} · ${selectedPlan === 'monthly' ? '1.111' : '1.000'}/mes
                </p>
              </div>

              <h3 className="text-xl font-medium text-gray-900 mb-2">Antes de continuar</h3>
              <p className="text-sm text-gray-600 mb-6">
                ¿Cómo prefieres continuar?
              </p>

              <div className="space-y-3">
                <button
                  onClick={() => setStep('login')}
                  className="w-full py-3.5 border-2 border-sb-300 text-sb-700 rounded-xl font-semibold text-sm tracking-wide transition-all hover:border-sb-500 hover:bg-sb-50"
                >
                  ✓ Ya tengo cuenta · Iniciar sesión
                </button>

                <button
                  onClick={() => setStep('register')}
                  className="w-full py-3.5 bg-gradient-to-r from-so-600 to-so-400 text-white rounded-xl font-semibold text-sm tracking-wide transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5"
                >
                  + Soy nueva · Crear cuenta
                </button>
              </div>
            </>
          ) : step === 'login' ? (
            <>
              <div className="mb-4">
                <button
                  onClick={() => setStep('auth')}
                  className="inline-flex items-center gap-2 text-xs font-semibold tracking-wider uppercase text-sb-600 hover:text-sb-800"
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M15 18l-6-6 6-6" />
                  </svg>
                  Regresar
                </button>
              </div>

              <h3 className="text-xl font-medium text-gray-900 mb-2">Iniciar sesión</h3>
              <p className="text-sm text-gray-600 mb-6">
                Ingresa tus datos para conectarte a tu cuenta.
              </p>

              {loginError && (
                <div className="p-3 mb-4 rounded-lg bg-red-50 border border-red-200">
                  <p className="text-sm text-red-700">{loginError}</p>
                </div>
              )}

              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold tracking-wide text-gray-700 mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={loginData.email}
                    onChange={handleLoginChange}
                    placeholder="tu@email.com"
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-sb-600 focus:ring-2 focus:ring-sb-200 transition-all"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold tracking-wide text-gray-700 mb-2">
                    Contraseña
                  </label>
                  <div className="relative">
                    <input
                      type={showLoginPassword ? 'text' : 'password'}
                      name="password"
                      value={loginData.password}
                      onChange={handleLoginChange}
                      placeholder="Tu contraseña"
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-sb-600 focus:ring-2 focus:ring-sb-200 transition-all pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowLoginPassword(!showLoginPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 text-sm"
                    >
                      {showLoginPassword ? '🙈' : '👁️'}
                    </button>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={handleLogin}
                  disabled={isLoading}
                  className="w-full py-3.5 bg-gradient-to-r from-so-600 to-so-400 text-white rounded-xl font-semibold text-sm tracking-wide transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5 disabled:opacity-70 disabled:cursor-not-allowed"
                >
                  {isLoading ? 'Conectando...' : 'Iniciar sesión'}
                </button>

                <p className="text-center text-xs text-gray-500">
                  ¿No tienes cuenta?{' '}
                  <button
                    type="button"
                    onClick={() => setStep('register')}
                    className="text-sb-600 hover:underline font-semibold"
                  >
                    Crea una aquí
                  </button>
                </p>
              </div>
            </>
          ) : step === 'register' ? (
            <>
              <div className="mb-4">
                <button
                  onClick={() => setStep('plan')}
                  className="inline-flex items-center gap-2 text-xs font-semibold tracking-wider uppercase text-sb-600 hover:text-sb-800"
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M15 18l-6-6 6-6" />
                  </svg>
                  Regresar a planes
                </button>
              </div>

              <h3 className="text-xl font-medium text-gray-900 mb-2">Crea tu cuenta</h3>
              <p className="text-sm text-gray-600 mb-6">
                Necesitamos algunos datos para crear tu cuenta de acceso seguro.
              </p>

              {registerError && (
                <div className="p-3 mb-4 rounded-lg bg-red-50 border border-red-200">
                  <p className="text-sm text-red-700">{registerError}</p>
                </div>
              )}

              <form className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold tracking-wide text-gray-700 mb-2">
                      Nombre
                    </label>
                    <input
                      type="text"
                      name="firstName"
                      value={registerData.firstName}
                      onChange={handleRegisterChange}
                      placeholder="Tu nombre"
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-sb-600 focus:ring-2 focus:ring-sb-200 transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold tracking-wide text-gray-700 mb-2">
                      Apellido
                    </label>
                    <input
                      type="text"
                      name="lastName"
                      value={registerData.lastName}
                      onChange={handleRegisterChange}
                      placeholder="Tu apellido"
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-sb-600 focus:ring-2 focus:ring-sb-200 transition-all"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold tracking-wide text-gray-700 mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={registerData.email}
                    onChange={handleRegisterChange}
                    placeholder="tu@email.com"
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-sb-600 focus:ring-2 focus:ring-sb-200 transition-all"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold tracking-wide text-gray-700 mb-2">
                    Contraseña
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      name="password"
                      value={registerData.password}
                      onChange={handleRegisterChange}
                      placeholder="Min. 6 caracteres"
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-sb-600 focus:ring-2 focus:ring-sb-200 transition-all pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 text-sm"
                    >
                      {showPassword ? '🙈' : '👁️'}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold tracking-wide text-gray-700 mb-2">
                    Confirmar contraseña
                  </label>
                  <input
                    type="password"
                    name="confirmPassword"
                    value={registerData.confirmPassword}
                    onChange={handleRegisterChange}
                    placeholder="Repite tu contraseña"
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-sb-600 focus:ring-2 focus:ring-sb-200 transition-all"
                  />
                </div>

                <button
                  type="button"
                  onClick={handleRegister}
                  disabled={isLoading}
                  className="w-full py-3.5 bg-gradient-to-r from-so-600 to-so-400 text-white rounded-xl font-semibold text-sm tracking-wide transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5 disabled:opacity-70 disabled:cursor-not-allowed"
                >
                  {isLoading ? 'Creando cuenta...' : 'Crear cuenta'}
                </button>

                <p className="text-center text-xs text-gray-500 mt-4">
                  Al crear cuenta aceptas nuestros{' '}
                  <a href="#" className="text-sb-600 hover:underline">
                    Términos
                  </a>{' '}
                  y{' '}
                  <a href="#" className="text-sb-600 hover:underline">
                    Política de Privacidad
                  </a>
                  .
                </p>
              </form>
            </>
          ) : step === 'confirm' ? (
            <>
              <div className="text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-sb-100 flex items-center justify-center">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#B14A36" strokeWidth="2">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                    <circle cx="12" cy="7" r="4" />
                  </svg>
                </div>

                <h3 className="text-xl font-medium text-gray-900 mb-1">¡Bienvenido!</h3>
                <p className="text-sm text-gray-600 mb-6">
                  <strong>{getDisplayName()}</strong>
                </p>

                <div className="rounded-2xl border border-sb-200 bg-sb-50 p-4 mb-6">
                  <p className="text-xs uppercase tracking-widest text-sb-600 font-semibold mb-2">Tu plan</p>
                  <p className="text-base font-semibold text-gray-900 mb-1">
                    {selectedPlan === 'monthly' ? 'Mujer Soberana Mensual' : 'Mujer Soberana Trimestral'}
                  </p>
                  <p className="text-sm text-gray-600">
                    {selectedPlan === 'monthly' ? '$1.111/mes' : '$3.000 por 3 meses ($1.000/mes)'}
                  </p>
                </div>

                <button
                  onClick={handleConfirmAndCheckout}
                  disabled={isLoading}
                  className="w-full py-4 bg-gradient-to-r from-so-600 to-so-400 text-white rounded-xl font-semibold text-sm tracking-wide transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5 disabled:opacity-70 disabled:cursor-not-allowed"
                >
                  {isLoading ? '✓ Redirigiendo al pago seguro…' : 'Confirmar y continuar al pago'}
                </button>

                <p className="text-center text-xs text-gray-500 mt-4">
                  Al continuar aceptas nuestros{' '}
                  <a href="#" className="text-sb-600 hover:underline">
                    Términos
                  </a>{' '}
                  y{' '}
                  <a href="#" className="text-sb-600 hover:underline">
                    Política de Privacidad
                  </a>
                  .
                </p>
              </div>
            </>
          ) : (
            <div className="text-center">
              <p className="text-gray-600">Cargando...</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SubscriptionModal;
